// NerdTools converter core — pure, no UI. Covered by tests/toolstest.mjs
// against known conversion constants (NIST / exact SI definitions).

// Each category maps unit -> factor relative to a base unit.
// value_in_base = value * factor ; converted = value_in_base / target_factor
export const CONVERSIONS = {
  length: {
    base: "meter",
    units: {
      meter: 1,
      kilometer: 1000,
      centimeter: 0.01,
      millimeter: 0.001,
      mile: 1609.344,
      yard: 0.9144,
      foot: 0.3048,
      inch: 0.0254,
      nauticalMile: 1852,
    },
  },
  mass: {
    base: "kilogram",
    units: {
      kilogram: 1,
      gram: 0.001,
      milligram: 1e-6,
      tonne: 1000,
      pound: 0.45359237,
      ounce: 0.028349523125,
      stone: 6.35029318,
    },
  },
  volume: {
    base: "liter",
    units: {
      liter: 1,
      milliliter: 0.001,
      cubicMeter: 1000,
      gallonUS: 3.785411784,
      quartUS: 0.946352946,
      pintUS: 0.473176473,
      cupUS: 0.2365882365,
      fluidOunceUS: 0.0295735295625,
      tablespoonUS: 0.01478676478125,
      teaspoonUS: 0.00492892159375,
    },
  },
  speed: {
    base: "meterPerSecond",
    units: {
      meterPerSecond: 1,
      kilometerPerHour: 1 / 3.6,
      milePerHour: 0.44704,
      knot: 0.514444444444,
      footPerSecond: 0.3048,
    },
  },
  area: {
    base: "squareMeter",
    units: {
      squareMeter: 1,
      squareKilometer: 1e6,
      squareFoot: 0.09290304,
      squareMile: 2589988.110336,
      acre: 4046.8564224,
      hectare: 10000,
    },
  },
  dataStorage: {
    base: "byte",
    units: {
      bit: 0.125,
      byte: 1,
      kilobyte: 1e3,
      megabyte: 1e6,
      gigabyte: 1e9,
      terabyte: 1e12,
      kibibyte: 1024,
      mebibyte: 1024 ** 2,
      gibibyte: 1024 ** 3,
    },
  },
};

export function convert(category, value, fromUnit, toUnit) {
  const cat = CONVERSIONS[category];
  if (!cat) return { ok: false, error: `Unknown category: ${category}` };
  const f = cat.units[fromUnit];
  const t = cat.units[toUnit];
  if (f == null || t == null) return { ok: false, error: "Unknown unit" };
  if (typeof value !== "number" || !isFinite(value)) return { ok: false, error: "Invalid value" };
  return { ok: true, result: (value * f) / t };
}

// Temperature is affine, not multiplicative — handled separately.
export function convertTemperature(value, from, to) {
  if (typeof value !== "number" || !isFinite(value)) return { ok: false, error: "Invalid value" };
  let c; // to Celsius
  if (from === "celsius") c = value;
  else if (from === "fahrenheit") c = (value - 32) * (5 / 9);
  else if (from === "kelvin") c = value - 273.15;
  else return { ok: false, error: "Unknown temperature unit" };
  let out;
  if (to === "celsius") out = c;
  else if (to === "fahrenheit") out = c * (9 / 5) + 32;
  else if (to === "kelvin") out = c + 273.15;
  else return { ok: false, error: "Unknown temperature unit" };
  return { ok: true, result: out };
}

// dB from amplitude ratio (used by the decibel meter): 20*log10(p/p0)
export function amplitudeToDb(amplitude, reference = 1) {
  if (amplitude <= 0 || reference <= 0) return null;
  return 20 * Math.log10(amplitude / reference);
}

export function round(x, p = 6) {
  if (typeof x !== "number" || !isFinite(x)) return x;
  const f = Math.pow(10, p);
  return Math.round(x * f) / f;
}

